import { create } from 'zustand';
import { sessionBundleAPI } from '../services/api';
import { SessionBundle, PromotionDetails } from '@shared/types';
import { getCachedData, setCachedData } from '../utils/cache';

interface SessionBundlesCacheData {
  sessionBundles: SessionBundle[];
  promotionDetails: PromotionDetails | null;
}

interface SessionBundlesStoreState {
  sessionBundles: SessionBundle[];
  promotionDetails: PromotionDetails | null;
  loading: boolean;
  error: string | null;
  lastFetchTime: number | null;
  fetchSessionBundles: () => Promise<SessionBundlesCacheData | null>;
}

let pendingFetch: Promise<SessionBundlesCacheData | null> | null = null;

const CACHE_KEY = 'sessionBundles';
const CACHE_TTL = 60000;

export const useSessionBundlesStore = create<SessionBundlesStoreState>((set, get) => ({
  sessionBundles: getCachedData<SessionBundlesCacheData>(CACHE_KEY)?.sessionBundles || [],
  promotionDetails: getCachedData<SessionBundlesCacheData>(CACHE_KEY)?.promotionDetails || null,
  loading: false,
  error: null,
  lastFetchTime: null,

  fetchSessionBundles: async () => {
    const state = get();
    const now = Date.now();

    if (
      state.lastFetchTime &&
      now - state.lastFetchTime < CACHE_TTL &&
      state.sessionBundles.length > 0
    ) {
      return { sessionBundles: state.sessionBundles, promotionDetails: state.promotionDetails };
    }

    if (pendingFetch) {
      return pendingFetch;
    }

    set({ loading: true, error: null });

    pendingFetch = (async () => {
      try {
        const response = await sessionBundleAPI.getAll();
        const apiData = (response.data as any)?.data;

        let sessionBundles: SessionBundle[] = [];
        let promotionDetails: PromotionDetails | null = null;

        if (apiData && apiData.sessionBundles && Array.isArray(apiData.sessionBundles)) {
          sessionBundles = apiData.sessionBundles;
          promotionDetails = apiData.promotionDetails || null;
        } else if (response.data && Array.isArray(response.data)) {
          sessionBundles = response.data;
        }

        const newData: SessionBundlesCacheData = { sessionBundles, promotionDetails };
        setCachedData(CACHE_KEY, newData);

        set({
          sessionBundles,
          promotionDetails,
          loading: false,
          error: null,
          lastFetchTime: Date.now(),
        });

        return newData;
      } catch (err) {
        console.error('Failed to fetch sessionBundles:', err);
        const cachedData = getCachedData<SessionBundlesCacheData>(CACHE_KEY);

        if (cachedData) {
          set({
            sessionBundles: cachedData.sessionBundles,
            promotionDetails: cachedData.promotionDetails,
            loading: false,
            error: null,
          });
          return cachedData;
        }

        set({ loading: false, error: 'Failed to fetch sessionBundles' });
        return null;
      } finally {
        pendingFetch = null;
      }
    })();

    return pendingFetch;
  },
}));
